// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

// "Select area" on the 3D viewer: the user drags a rectangle, and we hand back a WGS84 region payload
// (bbox + GeoJSON polygon + acreage) that feeds app_state.selected_region — so Khora's query_region tool
// can scope "in this area / here" answers to it. Isolated from the 4k-line viewer so the geometry/CRS
// conversion is a small, self-contained unit; the viewer only creates the controller and wires a button.

import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import SketchViewModel from '@arcgis/core/widgets/Sketch/SketchViewModel';
import SimpleFillSymbol from '@arcgis/core/symbols/SimpleFillSymbol';
import * as webMercatorUtils from '@arcgis/core/geometry/support/webMercatorUtils';
import * as geometryEngine from '@arcgis/core/geometry/geometryEngine';
import Polygon from '@arcgis/core/geometry/Polygon';
import SceneView from '@arcgis/core/views/SceneView';
import MapView from '@arcgis/core/views/MapView';
import { SelectedRegion } from 'Features/dashboard/dashboardSlice';

// Draw shapes the region tool supports — matches the Lambda trigger_app_action `region_mode` enum, so a
// Khora-initiated draw_region can pick the shape. The "Select area" button always uses 'rectangle'.
export type RegionDrawMode = 'rectangle' | 'polygon' | 'freehand';

// Convert a drawn viewer polygon into the WGS84 region payload query_region expects: bbox is
// [west, south, east, north] and geometry is a closed GeoJSON polygon, both in lon/lat degrees;
// area_acres is a geodesic measurement (SR-independent) for the readout. Returns null for an empty or
// unreprojectable polygon so the caller never stores a half-formed region.
export const esriPolygonToWgs84Region = (polygon: Polygon | null): SelectedRegion | null => {
  if (!polygon || !polygon.rings || polygon.rings.length === 0) return null;
  // Measure BEFORE reprojection — geodesicArea is accurate in the polygon's own SR.
  const areaAcres = Math.abs(geometryEngine.geodesicArea(polygon, 'acres'));
  // The viewer draws in Web Mercator; query_region wants WGS84. A polygon already in WGS84 is used as-is.
  const sr = polygon.spatialReference;
  const geographic = (sr && (sr.isWGS84 || sr.wkid === 4326)
    ? polygon
    : (webMercatorUtils.webMercatorToGeographic(polygon) as Polygon | null));
  if (!geographic || !geographic.rings || geographic.rings.length === 0 || !geographic.extent) return null;
  const { extent } = geographic;
  return {
    bbox: [extent.xmin, extent.ymin, extent.xmax, extent.ymax],
    // Drop any Z; GeoJSON polygon rings are [lon, lat]. Esri rings are already closed (first == last).
    geometry: { type: 'Polygon', coordinates: geographic.rings.map((ring) => ring.map((pt) => [pt[0], pt[1]])) },
    area_acres: Math.round(areaAcres * 100) / 100,
  };
};

// A handle the viewer keeps to drive region selection: start a rectangle draw, abort an in-progress draw,
// clear the drawn area entirely, or tear everything down on unmount. `onComplete` fires once the user
// finishes a rectangle; `onActiveChange` tracks whether a draw is in progress (for a Cancel affordance).
export interface RegionSketchController {
  startDraw: (mode?: RegionDrawMode) => void; // begin drawing a region (default 'rectangle')
  startEdit: () => void; // enter move/resize/reshape on the drawn region (opt-in — clicks otherwise pass through)
  cancelDraw: () => void; // abort an in-progress draw only — a COMPLETED region graphic is left intact
  clear: () => void; // remove the drawn graphic entirely (also cancels any in-progress draw)
  destroy: () => void;
}

// AirWorks bright blue (#01A1DD) — a translucent fill with a solid outline for the drawn area.
const REGION_FILL: [number, number, number, number] = [1, 161, 221, 0.15];
const REGION_OUTLINE: [number, number, number, number] = [1, 161, 221, 1];

export const createRegionSketchController = (
  view: SceneView | MapView,
  onComplete: (region: SelectedRegion) => void,
  onActiveChange?: (active: boolean) => void,
  // Fired when a draw is CANCELLED — startDraw already erased any prior region's graphic, so the caller must
  // drop its stored region too, else a cancelled re-draw leaves a phantom region still sent to Khora.
  onClear?: () => void,
): RegionSketchController => {
  const layer = new GraphicsLayer({ listMode: 'hide', elevationInfo: { mode: 'on-the-ground' } });
  view.map.add(layer);
  const sketch = new SketchViewModel({
    view,
    layer,
    polygonSymbol: new SimpleFillSymbol({ color: REGION_FILL, outline: { color: REGION_OUTLINE, width: 2 } }),
    // Editing is OPT-IN (the "Edit" button calls startEdit -> sketch.update), NOT click-to-edit: a filled
    // region graphic with updateOnGraphicClick would intercept every map click inside it (its translucent
    // interior is still hit-testable), blocking feature selection under the region — the exact draw-around-a-
    // cluster-then-inspect flow query_region is for. So a map click always falls through to feature selection.
    updateOnGraphicClick: false,
  });
  // Recompute + publish selected_region from a polygon — the freshly-drawn one, or one the user just edited.
  const emitRegion = (geometry: Polygon | null | undefined) => {
    const region = esriPolygonToWgs84Region(geometry ?? null);
    if (region) onComplete(region);
  };
  const createHandle = sketch.on('create', (event) => {
    if (event.state === 'start') { onActiveChange?.(true); return; }
    if (event.state === 'cancel') { onActiveChange?.(false); onClear?.(); return; }
    if (event.state !== 'complete') return;
    onActiveChange?.(false);
    emitRegion(event.graphic.geometry as Polygon);
  });
  // The user moved / resized / reshaped the drawn region — keep selected_region (and the chip's acreage) in
  // step as they drag ('active'), when they finish ('complete'), AND when they abandon an edit ('cancel',
  // e.g. Escape / a tool switch), which reverts the graphic and must re-sync Redux to that reverted shape so
  // the map and selected_region never diverge. 'start' carries no geometry change yet. Editing keeps
  // selectedRegion truthy, so the reconciliation effect never treats it as cleared.
  // `clear()` ALSO cancels the sketch, but there the reverted geometry must NOT be re-published (clear wants
  // the region gone) — suppressClearEmit swallows that one event; every other cancel still re-syncs.
  let suppressClearEmit = false;
  let activeEmitRaf = 0;
  const cancelActiveEmit = () => { if (activeEmitRaf) { cancelAnimationFrame(activeEmitRaf); activeEmitRaf = 0; } };
  const updateHandle = sketch.on('update', (event) => {
    if (event.state === 'start' || suppressClearEmit) return;
    if (event.state === 'active') {
      // Coalesce the continuous drag stream to one emit per frame — a heavy freehand region would otherwise
      // reproject + re-publish (re-rendering the viewer) on every pointer-move. The graphic mutates in place,
      // so the deferred read picks up the latest geometry.
      if (activeEmitRaf) return;
      activeEmitRaf = requestAnimationFrame(() => {
        activeEmitRaf = 0;
        emitRegion(event.graphics[0]?.geometry as Polygon);
      });
      return;
    }
    // 'complete' | 'cancel' — publish the final (or reverted) geometry now; drop any pending drag frame.
    cancelActiveEmit();
    emitRegion(event.graphics[0]?.geometry as Polygon);
  });
  return {
    startDraw: (mode: RegionDrawMode = 'rectangle') => {
      cancelActiveEmit(); // a fresh draw supersedes any pending edit-drag emit
      layer.removeAll();
      // SketchViewModel draws a freehand region as a polygon in freehand mode; rectangle/polygon are tools.
      if (mode === 'freehand') sketch.create('polygon', { mode: 'freehand' });
      else sketch.create(mode);
    },
    // Enter edit mode on the drawn region on demand (from the chip's "Edit" button) — since clicks don't
    // arm editing, this is the ONLY way in. No-op when nothing is drawn.
    startEdit: () => { const graphic = layer.graphics.getItemAt(0); if (graphic) sketch.update(graphic); },
    // cancel() aborts an in-progress DRAW (discarding it) or reverts an in-progress EDIT; the update handler
    // re-syncs selected_region to a reverted edit, so tool-switching mid-edit never desyncs the map from Redux.
    cancelDraw: () => { sketch.cancel(); },
    // Remove the region entirely: cancel any pending drag frame FIRST (else it fires after removeAll and
    // resurrects the region — suppressClearEmit only silences the sketch.cancel event, not the queued rAF),
    // then swallow the cancel's revert-emit (we want it gone) and drop the graphic.
    clear: () => { cancelActiveEmit(); suppressClearEmit = true; sketch.cancel(); layer.removeAll(); suppressClearEmit = false; },
    destroy: () => {
      cancelActiveEmit();
      createHandle.remove(); updateHandle.remove(); sketch.destroy(); view.map?.remove(layer);
    },
  };
};
